"use client";

import Link from "next/link";
import { motion, type HTMLMotionProps } from "motion/react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

const animationProps = {
  initial: { "--x": "100%", scale: 0.98 },
  animate: { "--x": "-100%", scale: 1 },
  whileTap: { scale: 0.97 },
  transition: {
    repeat: Infinity,
    repeatType: "loop" as const,
    repeatDelay: 1.4,
    type: "spring" as const,
    stiffness: 20,
    damping: 15,
    mass: 2,
    scale: {
      type: "spring" as const,
      stiffness: 200,
      damping: 5,
      mass: 0.5,
    },
  },
};

// Created once at module scope so the component identity is stable across
// renders. Using next/link (instead of a plain <a>) means clicks become
// client-side soft navigations, which keeps the rest of the React tree alive
// and lets the browser back button restore correctly without a hard reload.
const MotionLink = motion.create(Link);

type ShinyButtonProps = {
  children: ReactNode;
  className?: string;
  href?: string;
} & Omit<HTMLMotionProps<"button">, "ref" | "children">;

/**
 * Magicui "shiny button" — a soft, slowly-shimmering CTA pill. The shimmer is
 * a mask gradient that sweeps the text/icon, not a border beam.
 */
export function ShinyButton({
  children,
  className,
  href,
  ...props
}: ShinyButtonProps) {
  const inner = (
    <span
      className="relative inline-flex h-full w-full items-center justify-center text-[15px] tracking-tight font-semibold text-neutral-950"
      style={{
        maskImage:
          "linear-gradient(-75deg,#000 calc(var(--x) + 20%),transparent calc(var(--x) + 30%),#000 calc(var(--x) + 100%))",
        WebkitMaskImage:
          "linear-gradient(-75deg,#000 calc(var(--x) + 20%),transparent calc(var(--x) + 30%),#000 calc(var(--x) + 100%))",
      }}
    >
      {children}
    </span>
  );

  const baseClasses = cn(
    "relative inline-flex h-[52px] items-center justify-center overflow-hidden rounded-lg px-6",
    "border border-neutral-200 bg-white",
    "shadow-[0_1px_2px_rgba(14,18,27,0.04)]",
    "transition-shadow duration-300 ease-in-out hover:shadow-[0_8px_22px_-12px_rgba(14,18,27,0.18)]",
    className,
  );

  if (href !== undefined) {
    return (
      <MotionLink {...animationProps} href={href} className={baseClasses}>
        {inner}
      </MotionLink>
    );
  }

  return (
    <motion.button {...animationProps} {...props} className={baseClasses}>
      {inner}
    </motion.button>
  );
}
